
import React, { useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabaseWorker } from "@/integrations/supabase/workerClient";
import { ClientSelectionSection } from '@/components/common/dialogs/sections/ClientSelectionSection';
import { LocationSelectionSection } from '@/components/common/dialogs/sections/LocationSelectionSection';
import { InventorySelectionSection } from './sections/InventorySelectionSection';
import { StoreItemsSection } from './sections/StoreItemsSection';
import { RentalKitsSection } from './sections/RentalKitsSection';
import { OrderSummarySection } from './sections/OrderSummarySection';
import { FinancialSection } from '@/components/common/dialogs/sections/FinancialSection';
import { DateSelectionSection } from '@/components/common/dialogs/sections/DateSelectionSection';
import { StatusSelectionSection } from '@/components/common/dialogs/sections/StatusSelectionSection';
import { DocumentSignatureSection } from '@/components/orders/dialogs/components/sections/DocumentSignatureSection';
import { useOrderStatuses } from '@/hooks/domain-statuses/useOrderStatuses';
import { usePaymentStatuses } from '@/hooks/domain-statuses/usePaymentStatuses';
import { usePaymentMethods } from '@/hooks/domain-statuses/usePaymentMethods';
import type { OrderFormData, LastAction } from '@/domains/orders/hooks/types';
import type { Client } from '@/types/entities/client';
import type { OrderStoreItem } from '@/hooks/useOrderStoreItems';
import { RentalProduct, Location } from '@/types/entities';

interface OrderDialogSectionsProps {
  formData: OrderFormData;
  selectedKits: string[];
  selectedStoreItems: Array<{ id: string; quantity: number }>;
  clients: Client[];
  clientsLoading: boolean;
  currentRentalPoint: Location | null;
  locations: Location[];
  totalAmount: number;
  lastAction?: LastAction | null;
  inventoryItems: RentalProduct[];
  inventoryLoading: boolean;
  storeItems: OrderStoreItem[];
  storeItemsLoading: boolean;
  rentalKits: Array<{ id: string; name: string }>;
  kitsLoading: boolean;
  isStorePurchase: boolean;
  isEditMode?: boolean; // Добавляем параметр для различения создания и редактирования
  onClientChange: (clientId: string) => void;
  onAddClient: () => void;
  onDateChange: (field: 'startDate' | 'endDate', value: Date | undefined) => void;
  onKitsChange: (kits: string[]) => void;
  onInventoryItemChange: (id: string, quantity: number) => void;
  onStoreItemChange: (id: string, quantity: number) => void;
  onUndoLastAction: () => void;
  onFieldChange: <K extends keyof OrderFormData>(field: K, value: OrderFormData[K]) => void;
}

export const OrderDialogSections: React.FC<OrderDialogSectionsProps> = ({
  formData,
  selectedKits,
  selectedStoreItems,
  clients,
  clientsLoading,
  currentRentalPoint,
  locations,
  totalAmount,
  lastAction,
  inventoryItems,
  inventoryLoading,
  storeItems,
  storeItemsLoading,
  rentalKits,
  kitsLoading,
  isStorePurchase,
  isEditMode = false,
  onClientChange,
  onAddClient,
  onDateChange,
  onKitsChange,
  onInventoryItemChange,
  onStoreItemChange,
  onUndoLastAction,
  onFieldChange
}) => {
  console.log('[OrderDialogSections] render', {
    isEditMode,
    formDataSummary: {
      clientId: formData?.clientId,
      rentalPointId: formData?.rentalPointId,
      returnPointId: formData?.returnPointId,
      inventoryItemsCount: formData?.inventoryItems?.length || 0,
    }
  });
  // Загружаем все пункты проката для выбора пункта возврата (как в диалоге аренды)
  const {
    data: rentalPoints = []
  } = useQuery({
    queryKey: ['rental-points'],
    queryFn: async () => {
      console.log('[OrderDialogSections] useQuery rental-points: start fetch');
      const {
        data,
        error
      } = await supabaseWorker.from('rental_points').select('id, name, address').order('name');
      if (error) throw error;
      type RentalPointRow = { id: string; name: string; address?: string | null };
      const rows = (data ?? []) as RentalPointRow[];
      console.log('[OrderDialogSections] useQuery rental-points: fetched', { count: rows.length });
      return rows;
    }
  });
  console.log('[OrderDialogSections] useQuery rental-points: data', { count: rentalPoints.length });

  // Load domain statuses from new system
  const { orderStatuses = [], isLoading: statusesLoading } = useOrderStatuses();
  const { paymentStatuses = [], isLoading: paymentStatusesLoading } = usePaymentStatuses();
  const { paymentMethods = [], isLoading: paymentMethodsLoading } = usePaymentMethods();

  // In edit mode, if current values are legacy strings (not UUID), try to map by name to domain IDs
  useEffect(() => {
    if (!isEditMode) return;
    const isUuid = (v?: string) => !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
    const normalize = (s: string) => s.trim().toLowerCase();
    // Map legacy english codes to canonical Russian names used in domain tags
    const legacyOrderStatusNameMap: Record<string, string> = {
      'active': 'Активный',
      'completed': 'Активный', // нет доменного «Завершен» в выпадающем списке, отображаем допустимый тег
      'pending': 'К сборке',
      'to_assemble': 'К сборке',
      'to-return': 'К сдаче',
      'to_return': 'К сдаче',
      'overdue': 'Просрочен',
    };
    const legacyPaymentStatusNameMap: Record<string, string> = {
      'unpaid': 'Не оплачено',
      'paid': 'Оплачено',
      'partially_paid': 'Частично оплачено',
    };
    // Map order status
    if (formData.status && !isUuid(formData.status) && orderStatuses.length) {
      const raw = String(formData.status);
      const candidateName = legacyOrderStatusNameMap[normalize(raw)] || raw;
      const found = orderStatuses.find(s => normalize(s.name) === normalize(candidateName));
      if (found && found.id !== formData.status) {
        onFieldChange('status', found.id);
      }
    }
    // Map payment status
    if (formData.paymentStatus && !isUuid(formData.paymentStatus) && paymentStatuses.length) {
      const raw = String(formData.paymentStatus);
      const candidateName = legacyPaymentStatusNameMap[normalize(raw)] || raw;
      const found = paymentStatuses.find(s => normalize(s.name) === normalize(candidateName));
      if (found && found.id !== formData.paymentStatus) {
        onFieldChange('paymentStatus', found.id);
      }
    }
    // Map payment method by name if not UUID
    if (formData.paymentMethodId && !isUuid(formData.paymentMethodId) && paymentMethods.length) {
      const found = paymentMethods.find(m => m.name.toLowerCase() === String(formData.paymentMethodId).toLowerCase());
      if (found && found.id !== formData.paymentMethodId) {
        onFieldChange('paymentMethodId', found.id);
      }
    }
  }, [isEditMode, formData.status, formData.paymentStatus, formData.paymentMethodId, orderStatuses, paymentStatuses, paymentMethods]);

  // Filter order statuses based on dialog mode
  const filteredOrderStatuses = isEditMode 
    ? orderStatuses // В режиме редактирования показываем все статусы
    : orderStatuses.filter(status => 
        status.name === 'К сборке' || status.name === 'Активный'
      ); // В режиме создания только "К сборке" и "Активный"

  // Convert to options format
  const orderStatusOptions = filteredOrderStatuses.map(status => ({
    value: status.id,
    label: status.name
  }));

  const paymentStatusOptions = paymentStatuses.map(status => ({
    value: status.id,
    label: status.name
  }));

  const paymentMethodOptions = paymentMethods.map(method => ({
    value: method.id,
    label: method.name
  }));

  return (
    <div className="space-y-6">
      <ClientSelectionSection
        selectedClientId={formData.clientId}
        clients={clients}
        onClientChange={onClientChange}
        onAddClient={onAddClient}
        isLoading={clientsLoading}
      />

      {/* Location Selection - выбор пункта проката и возврата */}
      <LocationSelectionSection
        rentalPointName={currentRentalPoint?.name || "Не выбран"}
        returnPointName={formData.returnPointId ? rentalPoints.find(p => p.id === formData.returnPointId)?.name || "Не выбран" : "Не выбран"}
        showReturnPoint={formData.showReturnPoint || false}
        onShowReturnPointChange={show => onFieldChange('showReturnPoint', show)}
        rentalPoints={rentalPoints}
        rentalPointId={formData.rentalPointId || ''}
        onRentalPointChange={(id) => onFieldChange('rentalPointId', id)}
        returnPointId={formData.returnPointId || ''}
        onReturnPointChange={(id) => onFieldChange('returnPointId', id)}
        allowRentalPointSelection={true}
        isLoading={false}
      />

      {/* Даты и тариф — как в диалоге аренды */}
      <DateSelectionSection
        tariffId={formData.tariffId}
        startDate={formData.startDate}
        endDate={formData.endDate}
        onTariffChange={(v) => onFieldChange('tariffId', v)}
        onStartDateChange={(d) => onDateChange('startDate', d)}
        onEndDateChange={(d) => onDateChange('endDate', d)}
      />

      {/* Статус и оплата — для заказов */}
      <StatusSelectionSection
        status={formData.status}
        paymentStatus={formData.paymentStatus}
        paymentMethodId={formData.paymentMethodId}
        statusOptions={orderStatusOptions}
        paymentStatusOptions={paymentStatusOptions}
        paymentMethodOptions={paymentMethodOptions}
        statusLabel="Статус заказа"
        onStatusChange={(v) => onFieldChange('status', v)}
        onPaymentStatusChange={(v) => onFieldChange('paymentStatus', v)}
        onPaymentMethodChange={(v) => onFieldChange('paymentMethodId', v)}
      />

      {!isEditMode && (
        <>
          <InventorySelectionSection
            selectedItems={formData.inventoryItems || []}
            availableInventoryItems={inventoryItems}
            inventoryLoading={inventoryLoading}
            lastAction={lastAction}
            onInventoryItemChange={onInventoryItemChange}
            onUndoLastAction={onUndoLastAction}
            readOnly={false}
          />

          <RentalKitsSection
            selectedKits={selectedKits}
            rentalKits={rentalKits}
            kitsLoading={kitsLoading}
            onKitsChange={onKitsChange}
          />

          <StoreItemsSection
            isStorePurchase={isStorePurchase}
            selectedItems={selectedStoreItems}
            storeItems={storeItems}
            storeItemsLoading={storeItemsLoading}
            locations={locations}
            currentRentalPoint={currentRentalPoint}
            onChange={onFieldChange}
            onStoreItemChange={onStoreItemChange}
            readOnly={false}
          />
        </>
      )}

      <FinancialSection
        discount={formData.discount || 0}
        depositAmount={formData.depositAmount || 0}
        promoCode={formData.promoCode || ''}
        totalAmount={totalAmount}
        onDiscountChange={(v) => onFieldChange('discount', v)}
        onDepositAmountChange={(v) => onFieldChange('depositAmount', v)}
        onPromoCodeChange={(v) => onFieldChange('promoCode', v)}
      />

      <OrderSummarySection
        totalAmount={totalAmount}
        selectedInventoryItems={formData.inventoryItems || []}
        selectedStoreItems={selectedStoreItems}
        selectedKits={selectedKits}
        inventoryItems={inventoryItems}
        storeItems={storeItems}
        rentalKits={rentalKits}
      />

      <DocumentSignatureSection
        signatureId={formData.signatureId || ''}
        clientId={formData.clientId}
        generateContract={('generateContract' in formData) ? Boolean(formData.generateContract) : false}
        onChange={onFieldChange}
      />
    </div>
  );
};
